import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import API_URL, { getUserPlatform } from '../api';
import AiReportView from './AiReportView';
import './TestResults.css';
import { resolveAssetUrl } from '../utils/assetUrl';

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('uk-UA', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

const initials = (...parts) =>
    parts.filter(Boolean).map(s => s[0]?.toUpperCase()).join('');

const getResultFirstName = (result) => result?.studentName || result?.playerName || '';
const getResultLastName = (result) => result?.studentLastName || result?.playerLastName || '';
const getResultCity = (result) => result?.studentCity || result?.playerCity || result?.city || '';
const getResultPosition = (result) => result?.studentPosition || result?.playerPosition || result?.position || '';
const getQuizQuestionFallback = (result, index) => result?.quizId?.questions?.[index] || null;
const getResolvedQuizAnswer = (answer, index, result) => {
    const fallbackQuestion = getQuizQuestionFallback(result, index);
    const fallbackOptions = Array.isArray(fallbackQuestion?.options) ? fallbackQuestion.options : [];
    const fallbackCorrectIndex = Number.isInteger(fallbackQuestion?.correctIndex) ? fallbackQuestion.correctIndex : null;
    return {
        ...answer,
        questionText: answer?.questionText || fallbackQuestion?.text || `Питання ${index + 1}`,
        correctAnswer: answer?.correctAnswer || (fallbackCorrectIndex !== null ? fallbackOptions[fallbackCorrectIndex] : '—') || '—',
        explanation: answer?.explanation || fallbackQuestion?.explanation || '',
        image: answer?.image || fallbackQuestion?.image || '',
        video: answer?.video || fallbackQuestion?.video || ''
    };
};

// ── Editable city field ───────────────────────────────────────────────────────

const EditableCity = ({ value, cities, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [selected, setSelected] = useState(value || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!selected.trim() || selected === value) { setEditing(false); return; }
        setSaving(true);
        try {
            await onSave(selected.trim());
            setEditing(false);
        } catch { /* error handled in parent */ }
        setSaving(false);
    };

    if (!editing) {
        return (
            <span className="tr-editable-city" onClick={() => setEditing(true)} title="Натисніть щоб змінити місто">
                {value || '—'} <span className="tr-edit-icon">✏️</span>
            </span>
        );
    }

    return (
        <span className="tr-city-editor">
            <select value={selected} onChange={e => setSelected(e.target.value)} className="tr-city-select" autoFocus>
                <option value="">Оберіть місто</option>
                {cities.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
            </select>
            <button className="tr-city-save" onClick={handleSave} disabled={saving}>{saving ? '...' : '✓'}</button>
            <button className="tr-city-cancel" onClick={() => { setSelected(value || ''); setEditing(false); }}>✕</button>
        </span>
    );
};

// ── Avatar ────────────────────────────────────────────────────────────────────

const Avatar = ({ text, passed }) => (
    <div className={`tr-avatar ${passed ? 'pass' : 'fail'}`}>
        {text || '?'}
    </div>
);

// ── Detail Modal ──────────────────────────────────────────────────────────────

const DetailModal = ({ show, onClose, children, title }) => {
    useEffect(() => {
        if (!show) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [show, onClose]);

    if (!show) return null;
    return (
        <div className="tr-overlay" onClick={onClose}>
            <div className="tr-modal" onClick={e => e.stopPropagation()}>
                <div className="tr-modal-header">
                    <span className="tr-modal-title">{title}</span>
                    <button className="tr-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="tr-modal-body">{children}</div>
            </div>
        </div>
    );
};

// ── Desk detail ──────────────────────────────────────────────────────────────

const DeskDetail = ({ item, cities, canEdit, onCityChange }) => {
    const nameMap = {};
    (item.targetItems || []).forEach(t => { if (t.type && t.name) nameMap[t.type] = t.name; });
    const getName = (i) => i.name || nameMap[i.type] || i.icon || i.type;

    const wrongItems = (item.userItems || []).filter(i => !i.isCorrect);
    const missingItems = (item.targetItems || []).filter(target =>
        !(item.userItems || []).some(ui => ui.type === target.type && ui.isCorrect)
    );
    return (
        <div className="tr-detail">
            <div className="tr-detail-meta">
                <MetaField label="Дата" value={formatDate(item.completedAt)} />
                <MetaField label="Ім'я" value={`${item.studentLastName} ${item.studentName}`} />
                <MetaField label="Місто" value={canEdit
                    ? <EditableCity value={item.studentCity} cities={cities} onSave={onCityChange} />
                    : item.studentCity} />
                <MetaField label="Посада" value={item.studentPosition || '—'} />
                <MetaField label="Шаблон" value={item.templateName} span={2} />
            </div>
            <ScoreBlock
                score={`${item.percentage}%`}
                sub={`${item.score} з ${item.total} правильно`}
                passed={item.passed}
                label={item.passed ? 'Пройдено' : 'Не здано'}
            />
            {(wrongItems.length > 0 || missingItems.length > 0) && (
                <ErrorSection>
                    {wrongItems.length > 0 && (
                        <ErrorGroup label={`❌ Неправильно розміщені (${wrongItems.length})`}>
                            {wrongItems.map((it, i) => (
                                <ErrorItem key={i} icon={it.icon} name={getName(it)}
                                    hint={`(${Math.round(it.x)}, ${Math.round(it.y)})`} type="wrong" />
                            ))}
                        </ErrorGroup>
                    )}
                    {missingItems.length > 0 && (
                        <ErrorGroup label={`⚠️ Пропущені предмети (${missingItems.length})`}>
                            {missingItems.map((it, i) => (
                                <ErrorItem key={i} icon={it.icon} name={getName(it)}
                                    hint={`(${Math.round(it.x)}, ${Math.round(it.y)})`} type="missing" />
                            ))}
                        </ErrorGroup>
                    )}
                </ErrorSection>
            )}
        </div>
    );
};

// ── Game detail ──────────────────────────────────────────────────────────────

const GameDetail = ({ item, cities, canEdit, onCityChange }) => (
    <div className="tr-detail">
        <div className="tr-detail-meta">
            <MetaField label="Дата" value={formatDate(item.completedAt)} />
            <MetaField label="Ім'я" value={`${item.playerLastName || item.studentLastName} ${item.playerName || item.studentName}`} />
            <MetaField label="Місто" value={canEdit
                ? <EditableCity value={item.playerCity || item.city} cities={cities} onSave={onCityChange} />
                : (item.playerCity || item.city)} />
            <MetaField label="Посада" value={item.playerPosition || item.position || '—'} />
            <MetaField label="Сценарій" value={item.scenarioTitle} />
            <MetaField label="Кінцівка" value={item.endingTitle || '—'} />
        </div>
        <ScoreBlock
            score={item.isWin ? '🎉' : '😔'}
            passed={item.isWin}
            label={item.isWin ? 'Перемога' : 'Поразка'}
        />
        {item.choicePath?.length > 0 && (
            <ErrorSection label={`🔍 Шлях вибору (${item.choicePath.length} кроків)`}>
                <div className="tr-path">
                    {item.choicePath.map((cp, i) => (
                        <div key={i} className="tr-path-step">
                            <div className="tr-path-num">{i + 1}</div>
                            <div className="tr-path-body">
                                {cp.nodeText && <div className="tr-path-node">{cp.nodeText}</div>}
                                <div className="tr-path-choice">→ {cp.choiceText}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </ErrorSection>
        )}
    </div>
);

// ── Quiz detail ──────────────────────────────────────────────────────────────

const VideoPlayerMini = ({ url }) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?([^&]+)/);
    if (ytMatch) return <iframe title="yt" width="100%" height="100%"
        src={`https://www.youtube.com/embed/${ytMatch[1]}`} frameBorder="0" allowFullScreen />;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return <iframe title="vimeo"
        src={`https://player.vimeo.com/video/${vimeoMatch[1]}`} width="100%" height="100%" frameBorder="0" allowFullScreen />;
    const src = resolveAssetUrl(url);
    return <video controls style={{ width: '100%', height: '100%' }}><source src={src} /></video>;
};

const QuizAnswersList = ({ answers, quiz }) => (
    <ErrorSection label="🔍 Аналіз відповідей">
        {answers.map((rawAnswer, i) => {
            const a = getResolvedQuizAnswer(rawAnswer, i, { quizId: quiz });
            return (
                <div key={i} className={`tr-answer ${a.isCorrect ? 'correct' : 'wrong'}`}>
                    <div className="tr-answer-q">Питання {i + 1}: {a.questionText || '—'}</div>
                    {a.image && (
                        <img src={resolveAssetUrl(a.image)}
                            alt="" className="tr-answer-img" />
                    )}
                    {a.video && (
                        <div className="tr-answer-video">
                            <VideoPlayerMini url={a.video} />
                        </div>
                    )}
                    <div className="tr-answer-row">
                        <div className="tr-answer-columns">
                            <span className={`tr-answer-badge ${a.isCorrect ? 'correct' : 'wrong'}`}>
                                {a.isCorrect ? '✅' : '❌'} Відповідь: {a.givenAnswer || '—'}
                            </span>
                            <span className="tr-answer-correct">✓ Правильна: {a.correctAnswer || '—'}</span>
                        </div>
                        {a.explanation && (
                            <div className="tr-answer-correction">
                                <div className="tr-answer-explain">{a.explanation}</div>
                            </div>
                        )}
                    </div>
                </div>
            );
        })}
    </ErrorSection>
);

const QuizDetail = ({ item, cities, canEdit, onCityChange }) => (
    <div className="tr-detail">
        <div className="tr-detail-meta">
            <MetaField label="Дата" value={formatDate(item.completedAt)} />
            <MetaField label="Ім'я" value={`${item.studentLastName} ${item.studentName}`} />
            <MetaField label="Місто" value={canEdit
                ? <EditableCity value={item.studentCity} cities={cities} onSave={onCityChange} />
                : item.studentCity} />
            <MetaField label="Посада" value={item.studentPosition || '—'} />
            <MetaField label="Квіз" value={item.quizId?.title || 'Видалений квіз'} span={2} />
        </div>
        <ScoreBlock
            score={`${item.percentage}%`}
            sub={`${item.score} з ${item.total} правильно`}
            passed={item.percentage >= 80}
            label={item.percentage >= 80 ? 'Пройдено' : 'Не здано'}
        />
        {item.answers?.length > 0 && <QuizAnswersList answers={item.answers} quiz={item.quizId} />}
    </div>
);

// ── Complex detail ────────────────────────────────────────────────────────────

const ComplexDetail = ({ item, cities, canEdit, onCityChange }) => {
    const [expanded, setExpanded] = useState(null);
    return (
        <div className="tr-detail">
            <div className="tr-detail-meta">
                <MetaField label="Дата" value={formatDate(item.completedAt)} />
                <MetaField label="Ім'я" value={`${item.studentLastName} ${item.studentName}`} />
                <MetaField label="Місто" value={canEdit
                    ? <EditableCity value={item.studentCity} cities={cities} onSave={onCityChange} />
                    : item.studentCity} />
                <MetaField label="Посада" value={item.studentPosition || '—'} />
                <MetaField label="Тест" value={item.complexTestId?.title || 'Видалений тест'} span={2} />
            </div>
            <ScoreBlock
                score={item.overallPassed ? '✅' : '❌'}
                passed={item.overallPassed}
                label={item.overallPassed ? 'Всі кроки пройдено' : 'Є провалені кроки'}
            />
            {item.steps?.length > 0 && (
                <ErrorSection label="Деталі кроків">
                    {item.steps.map((s, i) => (
                        <React.Fragment key={i}>
                            <div
                                className={`tr-step ${s.passed ? 'pass' : 'fail'} ${s.type === 'quiz' && s.answers ? 'clickable' : ''}`}
                                onClick={() => s.type === 'quiz' && s.answers && setExpanded(expanded === i ? null : i)}
                            >
                                <div className="tr-step-icon">
                                    {s.type === 'desk' ? '🖥️' : s.type === 'game' ? '🎮' : '📝'}
                                </div>
                                <div className="tr-step-name">{s.title || '—'}</div>
                                <div className="tr-step-score">{s.score}/{s.total} · {s.percentage}%</div>
                                <span className={`tr-pill ${s.passed ? 'pass' : 'fail'}`}>
                                    {s.passed ? 'OK' : '✗'}
                                </span>
                                {s.type === 'quiz' && s.answers && (
                                    <span className="tr-step-toggle">{expanded === i ? '▲' : '▼'}</span>
                                )}
                            </div>
                            {expanded === i && s.answers && (
                                <div className="tr-step-answers">
                                    <QuizAnswersList answers={s.answers} />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </ErrorSection>
            )}
        </div>
    );
};



// ── Pizza detail ─────────────────────────────────────────────────────────────

const PizzaDetail = ({ item, cities, canEdit, onCityChange }) => {
    return (
        <div className="tr-detail">
            <div className="tr-detail-meta">
                <MetaField label="Дата" value={formatDate(item.completedAt || item.createdAt)} />
                <MetaField label="Ім'я" value={`${item.studentLastName} ${item.studentName}`} />
                <MetaField label="Місто" value={canEdit
                    ? <EditableCity value={item.studentCity} cities={cities} onSave={onCityChange} />
                    : item.studentCity} />
                <MetaField label="Посада" value={item.studentPosition || '—'} />
                <MetaField label="Рецепт піци" value={item.templateTitle} span={2} />
            </div>

            <ScoreBlock
                score={`${item.percentage}%`}
                passed={item.passed}
                label={item.passed ? 'Пройдено' : 'Не здано'}
            />

            {item.details && (
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', margin: '1rem 0' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.5rem' }}>👨‍🍳 Обрані компоненти:</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.9rem' }}>
                        <div><strong>Основа (тісто):</strong> {item.details.crust === 'thin' ? '🥖 Тонка' : item.details.crust === 'thick' ? '🍞 Пухка' : '🌾 Цільнозернова'}</div>
                        <div><strong>Соус:</strong> {item.details.sauce === 'tomato' ? '🍅 Томатний' : item.details.sauce === 'white' ? '🥛 Вершковий' : item.details.sauce === 'pesto' ? '🌿 Песто' : '🍖 Барбекю'}</div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <strong>Топінги:</strong> {item.details.toppings && item.details.toppings.length > 0 
                                ? item.details.toppings.join(', ') 
                                : 'немає'}
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <strong>Парування напою:</strong> {item.details.drink || 'не обрано'}
                        </div>
                    </div>
                </div>
            )}

            {item.feedback && item.feedback.length > 0 && (
                <ErrorSection label="📋 Зауваження рецептури">
                    <ul style={{ paddingLeft: '1.2rem', margin: '0.5rem 0', color: '#fca5a5', fontSize: '0.95rem', lineHeight: '1.6' }}>
                        {item.feedback.map((fb, idx) => (
                            <li key={idx}>{fb}</li>
                        ))}
                    </ul>
                </ErrorSection>
            )}
        </div>
    );
};

// ── Shared detail sub-components ──────────────────────────────────────────────

const MetaField = ({ label, value, span }) => (
    <div className="tr-meta-field" style={span ? { gridColumn: `span ${span}` } : {}}>
        <span className="tr-meta-label">{label}</span>
        <span className="tr-meta-value">{value}</span>
    </div>
);

const ScoreBlock = ({ score, sub, passed, label }) => (
    <div className={`tr-score-block ${passed ? 'pass' : 'fail'}`}>
        <div className="tr-score-big">{score}</div>
        {sub && <div className="tr-score-sub">{sub}</div>}
        <span className={`tr-pill ${passed ? 'pass' : 'fail'}`}>{label}</span>
    </div>
);

const ErrorSection = ({ label, children }) => (
    <div className="tr-section">
        {label && <div className="tr-section-label">{label}</div>}
        {children}
    </div>
);

const ErrorGroup = ({ label, children }) => (
    <div className="tr-error-group">
        <div className="tr-error-group-label">{label}</div>
        {children}
    </div>
);

const ErrorItem = ({ icon, name, hint, type }) => (
    <div className={`tr-error-item ${type}`}>
        <span>{icon || '🍽️'} {name}</span>
        <span className="tr-error-hint">{hint}</span>
    </div>
);

// ── Result Card Row ───────────────────────────────────────────────────────────

const ResultCard = ({ onClick, passed, avatarText, name, sub, city, date, extra, score }) => (
    <div className={`tr-card ${passed ? 'pass' : 'fail'}`} onClick={onClick}>
        <Avatar text={avatarText} passed={passed} />
        <div className="tr-card-main">
            <div className="tr-card-name">{name}</div>
            <div className="tr-card-sub">{sub}</div>
        </div>
        <div className="tr-card-city">
            <span>{city}</span>
            <span className="tr-card-date">{date}</span>
        </div>
        {extra && <div className="tr-card-extra">{extra}</div>}
        <div className="tr-card-score">{score}</div>
        <div className="tr-card-chevron">›</div>
    </div>
);

// ── Group ─────────────────────────────────────────────────────────────────────

const Group = ({ icon, name, chips, children, isOpen, onToggle }) => (
    <div className="tr-group">
        <div className="tr-group-header" onClick={onToggle} role="button" tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()}>
            <div className="tr-group-left">
                <span className="tr-group-icon">{icon}</span>
                <span className="tr-group-name">{name}</span>
            </div>
            <div className="tr-group-right">
                <div className="tr-group-chips">{chips}</div>
                <span className={`tr-group-chevron ${isOpen ? 'open' : ''}`}>›</span>
            </div>
        </div>
        {isOpen && <div className="tr-group-body">{children}</div>}
    </div>
);

// ── Stats strip ───────────────────────────────────────────────────────────────

const StatsStrip = ({ stats, tab }) => {
    const normalized = useMemo(() => {
        if (!stats || !stats.total) return null;
        if (tab === 'game') {
            return [
                { label: 'Всього', value: stats.total },
                { label: 'Перемог', value: stats.passed, color: 'green' },
                { label: 'Поразок', value: stats.failed, color: 'red' },
                { label: 'Рейтинг', value: `${stats.passRate}%`, color: stats.passRate >= 80 ? 'green' : 'red' },
            ];
        }
        return [
            { label: 'Всього', value: stats.total },
            { label: 'Здали', value: stats.passed, color: 'green' },
            { label: 'Не здали', value: stats.failed, color: 'red' },
            { label: 'Успішність', value: `${stats.passRate}%`, color: stats.passRate >= 80 ? 'green' : 'red' },
            ...(stats.averagePercent !== null && stats.averagePercent !== undefined
                ? [{ label: 'Середній %', value: `${stats.averagePercent}%`, color: stats.averagePercent >= 80 ? 'green' : 'neutral' }]
                : []),
        ];
    }, [stats, tab]);

    if (!normalized) return null;
    return (
        <div className="tr-stats">
            {normalized.map((s, i) => (
                <div key={i} className={`tr-stat ${s.color || ''}`}>
                    <div className="tr-stat-value">{s.value}</div>
                    <div className="tr-stat-label">{s.label}</div>
                </div>
            ))}
        </div>
    );
};

// ── Головний компонент ────────────────────────────────────────────────────────

const TestResults = ({ user }) => {
    const [tab, setTab] = useState('desk');
    const [resultsByType, setResultsByType] = useState({
        desk: [],
        game: [],
        quiz: [],
        complex: [],
        pizza: []
    });
    const [overview, setOverview] = useState({
        desk: { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null },
        game: { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null },
        quiz: { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null },
        complex: { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null },
        pizza: { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null }
    });
    const [paginationByType, setPaginationByType] = useState({
        desk: { page: 1, limit: 50, total: 0, hasMore: false },
        game: { page: 1, limit: 50, total: 0, hasMore: false },
        quiz: { page: 1, limit: 50, total: 0, hasMore: false },
        complex: { page: 1, limit: 50, total: 0, hasMore: false },
        pizza: { page: 1, limit: 50, total: 0, hasMore: false }
    });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState({});
    const [sortOrder, setSortOrder] = useState('newest');

    const [filterCity, setFilterCity] = useState('');
    const [filterDays, setFilterDays] = useState(0);
    const [cities, setCities] = useState([]);

    const [detailItem, setDetailItem] = useState(null);
    const [detailType, setDetailType] = useState(null);
    const [exportScope, setExportScope] = useState('current');

    const [aiAnalysis, setAiAnalysis] = useState('');
    const [aiSummary, setAiSummary] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiModalOpen, setAiModalOpen] = useState(false);

    const isAdminCityOnly = user?.role !== 'superadmin' && user?.city;
    const deferredSearch = React.useDeferredValue(search);

    useEffect(() => {
        axios.get(`${API_URL}/cities${getUserPlatform() ? `?platform=${getUserPlatform()}` : ''}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(res => setCities(res.data)).catch(() => {});
    }, []);

    const buildReportParams = useCallback((extra = {}) => {
        const params = {
            city: filterCity || undefined,
            days: filterDays || undefined,
            search: deferredSearch || undefined,
            sortOrder
        };
        if (isAdminCityOnly && user?.city) {
            params.city = user.city;
        }
        return { ...params, ...extra };
    }, [deferredSearch, filterCity, filterDays, isAdminCityOnly, sortOrder, user?.city]);

    const fetchOverview = useCallback(async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
            const response = await axios.get(`${API_URL}/reports/overview`, {
                ...config,
                params: buildReportParams()
            });
            setOverview(prev => ({ ...prev, ...(response.data.overview || {}) }));
        } catch (err) {
            console.error('Error fetching overview:', err);
        }
    }, [buildReportParams]);

    const fetchResults = useCallback(async (type, page = 1, append = false) => {
        const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
        const response = await axios.get(`${API_URL}/reports/results`, {
            ...config,
            params: buildReportParams({ type, page, limit: 50 })
        });
        const payload = response.data || {};
        setResultsByType(prev => ({
            ...prev,
            [type]: append ? [...prev[type], ...(payload.items || [])] : (payload.items || [])
        }));
        setPaginationByType(prev => ({
            ...prev,
            [type]: payload.pagination || prev[type]
        }));
    }, [buildReportParams]);

    const refreshCurrentTab = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchOverview(),
                fetchResults(tab, 1, false)
            ]);
        } catch (err) {
            console.error('Error fetching report data:', err);
        } finally {
            setLoading(false);
        }
    }, [fetchOverview, fetchResults, tab]);

    useEffect(() => {
        refreshCurrentTab();
    }, [refreshCurrentTab]);

    // WebSocket real-time updates
    useEffect(() => {
        const token = localStorage.getItem('token');
        const socket = io(API_URL.replace('/api', ''), {
            transports: ['websocket'],
            forceNew: true,
            auth: { token }
        });

        socket.on('NEW_RESULT', (newResult) => {
            console.log('Real-time result received:', newResult);
            refreshCurrentTab();
        });

        return () => socket.disconnect();
    }, [refreshCurrentTab, user]);

    const openDetail = (type, item) => { setDetailType(type); setDetailItem(item); };
    const closeDetail = () => { setDetailItem(null); setDetailType(null); };

    const canEditCity = ['superadmin', 'admin', 'trainer'].includes(user?.role);

    const updateCity = async (type, id, newCity) => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const endpoints = {
            desk: `${API_URL}/test-results/${id}/city`,
            game: `${API_URL}/game-results/${id}/city`,
            quiz: `${API_URL}/quiz/results/${id}/city`,
            complex: `${API_URL}/complex-tests/results/${id}/city`,
            pizza: `${API_URL}/pizza-templates/results/${id}/city`,
        };
        await axios.patch(endpoints[type], { city: newCity }, config);
        // Update local state
        const cityField = type === 'game' ? 'city' : 'studentCity';
        const updateItem = (list) => list.map(r =>
            r._id === id ? { ...r, [cityField]: newCity, ...(type !== 'game' ? { city: newCity } : {}) } : r
        );
        setResultsByType(prev => ({ ...prev, [type]: updateItem(prev[type]) }));
        // Update detail modal item
        if (detailItem?._id === id) {
            setDetailItem(prev => ({ ...prev, [cityField]: newCity, ...(type !== 'game' ? { city: newCity } : {}) }));
        }
    };

    const toggleGroup = (name) =>
        setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));

    const isOpen = (name) => collapsed[name] !== true;

    const filteredDesk = resultsByType.desk || [];
    const filteredGame = resultsByType.game || [];
    const filteredQuiz = resultsByType.quiz || [];
    const filteredComplex = resultsByType.complex || [];
    const filteredPizza = resultsByType.pizza || [];

    const currentFiltered = tab === 'desk' ? filteredDesk
        : tab === 'game' ? filteredGame
        : tab === 'quiz' ? filteredQuiz
        : tab === 'complex' ? filteredComplex
        : filteredPizza;

    // Group by
    const makeGroups = (arr, keyFn) =>
        arr.reduce((acc, item) => {
            const k = keyFn(item);
            if (!acc[k]) acc[k] = [];
            acc[k].push(item);
            return acc;
        }, {});

    const deskGroups = makeGroups(filteredDesk, r => r.templateName || 'Без назви');
    const gameGroups = makeGroups(filteredGame, r => r.scenarioTitle || 'Без назви');
    const quizGroups = makeGroups(filteredQuiz, r => r.quizId?.title || 'Видалений квіз');
    const complexGroups = makeGroups(filteredComplex, r => r.complexTestId?.title || 'Видалений тест');
    const pizzaGroups = makeGroups(filteredPizza, r => r.templateTitle || 'Без назви');

    const currentGroups = tab === 'desk' ? deskGroups
        : tab === 'game' ? gameGroups
        : tab === 'quiz' ? quizGroups
        : tab === 'complex' ? complexGroups
        : pizzaGroups;
    const currentPagination = paginationByType[tab] || { page: 1, limit: 50, total: currentFiltered.length, hasMore: false };
    const currentOverview = overview[tab] || { total: 0, passed: 0, failed: 0, passRate: 0, averagePercent: null };
    const remainingCount = Math.max(0, (currentPagination.total || 0) - currentFiltered.length);

    const handleAiAnalyze = async () => {
        setAiLoading(true);
        setAiModalOpen(true);
        setAiAnalysis('');
        setAiSummary(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/ai/analyze`, {
                mode: 'results',
                city: filterCity || undefined,
                days: filterDays || undefined,
                tab,
            }, { headers: { Authorization: `Bearer ${token}` } });
            setAiAnalysis(res.data.analysis);
            setAiSummary(res.data.summary || null);
        } catch (err) {
            setAiAnalysis('Помилка аналізу: ' + (err.response?.data?.error || err.message));
        } finally {
            setAiLoading(false);
        }
    };

    const exportToExcel = async () => {
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const scope = exportScope === 'current' ? tab : exportScope;
            const createResponse = await axios.post(`${API_URL}/reports/export`, {
                ...buildReportParams(),
                scope
            }, { headers: { Authorization: `Bearer ${token}` } });

            const { jobId } = createResponse.data;
            let status = 'queued';
            let downloadUrl = null;

            while (status === 'queued' || status === 'running') {
                await new Promise(resolve => setTimeout(resolve, 1200));
                const pollResponse = await axios.get(`${API_URL}/reports/export/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                status = pollResponse.data.status;
                downloadUrl = pollResponse.data.downloadUrl;
                if (status === 'failed') {
                    throw new Error(pollResponse.data.error || 'Не вдалося сформувати експорт');
                }
            }

            if (downloadUrl) {
                const fileResponse = await axios.get(`${API_URL.replace(/\/api$/, '')}${downloadUrl}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });
                const blobUrl = window.URL.createObjectURL(new Blob([fileResponse.data]));
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `results_${scope}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }
        } catch (err) {
            console.error('Export error:', err);
            alert(err.response?.data?.error || err.message || 'Помилка експорту');
        } finally {
            setExporting(false);
        }
    };

    const tabs = [
        { key: 'desk', icon: '🍽️', label: 'Накриття столу', count: overview.desk?.total || 0 },
        { key: 'game', icon: '🎮', label: 'Гра (Choice)', count: overview.game?.total || 0 },
        { key: 'quiz', icon: '📝', label: 'Квізи', count: overview.quiz?.total || 0 },
        { key: 'complex', icon: '🧩', label: 'Комплексний', count: overview.complex?.total || 0 },
        { key: 'pizza', icon: '🍕', label: 'Конструктор піци', count: overview.pizza?.total || 0 }
    ];

    const handleLoadMore = async () => {
        if (!currentPagination.hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            await fetchResults(tab, currentPagination.page + 1, true);
        } catch (err) {
            console.error('Error loading more results:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="tr-page">
            {/* Header */}
            <div className="tr-header">
                <div className="tr-header-left">
                    <h2 className="tr-title">
                        Результати {isAdminCityOnly ? <span className="tr-city-badge">{user.city}</span> : ''}
                    </h2>
                </div>
                <div className="tr-header-right">
                    {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                        <>
                            <select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="tr-select" title="Місто">
                                <option value="">Всі міста</option>
                                {cities.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                            </select>
                            <select value={filterDays} onChange={e => setFilterDays(Number(e.target.value))} className="tr-select" title="Період">
                                <option value={0}>Весь час</option>
                                <option value={7}>7 днів</option>
                                <option value={14}>14 днів</option>
                                <option value={30}>30 днів</option>
                                <option value={90}>90 днів</option>
                                <option value={365}>Рік</option>
                            </select>
                        </>
                    )}
                    <select value={exportScope} onChange={e => setExportScope(e.target.value)} className="tr-select" title="Тип експорту">
                        <option value="current">Поточна вкладка</option>
                        <option value="all">Усе разом</option>
                        <option value="desk">Лише сервірування</option>
                        <option value="game">Лише гра</option>
                        <option value="quiz">Лише квізи</option>
                        <option value="complex">Лише комплексні</option>
                        <option value="pizza">Лише конструктор піци</option>
                    </select>
                    <button className="tr-btn tr-btn-ai" onClick={handleAiAnalyze} disabled={aiLoading}>
                        {aiLoading ? '⏳' : '🤖'} AI Аналізатор
                    </button>
                    <button className="tr-btn" onClick={exportToExcel} disabled={exporting}>
                        {exporting ? '⏳ Експорт...' : '📊 Excel'}
                    </button>
                    <button className="tr-btn" onClick={refreshCurrentTab}>🔄</button>
                </div>
            </div>

            {/* Tabs */}
            <div className="tr-tabs">
                {tabs.map(t => (
                    <button
                        key={t.key}
                        className={`tr-tab ${tab === t.key ? 'active' : ''}`}
                        onClick={() => { setTab(t.key); setSearch(''); }}
                    >
                        <span>{t.icon} {t.label}</span>
                        {t.count > 0 && <span className="tr-tab-count">{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* Stats strip */}
            {!loading && <StatsStrip stats={currentOverview} tab={tab} />}

            {/* Toolbar */}
            <div className="tr-toolbar">
                <div className="tr-search-wrap">
                    <span className="tr-search-icon">🔍</span>
                    <input
                        className="tr-search"
                        placeholder="Пошук за ім'ям або містом..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && <button className="tr-search-clear" onClick={() => setSearch('')}>✕</button>}
                </div>
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="tr-select">
                    <option value="newest">Спочатку нові</option>
                    <option value="oldest">Спочатку старі</option>
                </select>
                {currentPagination.total > 0 && (
                    <span className="tr-count-label">
                        Завантажено {currentFiltered.length} з {currentPagination.total}
                        {currentPagination.hasMore ? ' · решта нижче через "Показати ще"' : ''}
                    </span>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <div className="tr-loading">
                    <div className="tr-spinner" />
                    <span>Завантаження...</span>
                </div>
            ) : Object.keys(currentGroups).length === 0 ? (
                <div className="tr-empty">
                    <div className="tr-empty-icon">📭</div>
                    <div>{search ? 'Нічого не знайдено' : 'Результатів поки немає'}</div>
                </div>
            ) : (
                <div className="tr-groups">
                    {tab === 'desk' && Object.entries(deskGroups).map(([name, items]) => {
                        const passed = items.filter(r => r.passed).length;
                        const avg = Math.round(items.reduce((s, r) => s + r.percentage, 0) / items.length);
                        return (
                            <Group key={name} icon="🍽️" name={name} isOpen={isOpen(name)} onToggle={() => toggleGroup(name)}
                                chips={<>
                                    <Chip>{items.length} спроб</Chip>
                                    <Chip color="green">{passed} здали</Chip>
                                    <Chip color="blue">~{avg}%</Chip>
                                </>}
                            >
                                {items.map(r => (
                                    <ResultCard key={r._id} onClick={() => openDetail('desk', r)}
                                        passed={r.passed}
                                        avatarText={initials(r.studentLastName, r.studentName)}
                                        name={`${r.studentLastName} ${r.studentName}`}
                                        sub={r.studentPosition || '—'}
                                        city={r.studentCity}
                                        date={formatDate(r.completedAt)}
                                        score={<>
                                            <strong className={r.passed ? 'clr-green' : 'clr-red'}>{r.percentage}%</strong>
                                            <span className={`tr-pill ${r.passed ? 'pass' : 'fail'}`}>{r.passed ? 'Здано' : 'Не здано'}</span>
                                        </>}
                                    />
                                ))}
                            </Group>
                        );
                    })}

                    {tab === 'game' && Object.entries(gameGroups).map(([name, items]) => {
                        const wins = items.filter(r => r.isWin).length;
                        return (
                            <Group key={name} icon="🎮" name={name} isOpen={isOpen(name)} onToggle={() => toggleGroup(name)}
                                chips={<>
                                    <Chip>{items.length} проходжень</Chip>
                                    <Chip color="green">{wins} перемог</Chip>
                                    <Chip color="red">{items.length - wins} поразок</Chip>
                                </>}
                            >
                                {items.map(r => (
                                    <ResultCard key={r._id} onClick={() => openDetail('game', r)}
                                        passed={r.isWin}
                                        avatarText={initials(getResultLastName(r), getResultFirstName(r))}
                                        name={`${getResultLastName(r)} ${getResultFirstName(r)}`.trim()}
                                        sub={getResultPosition(r) || '—'}
                                        city={getResultCity(r) || '—'}
                                        date={formatDate(r.completedAt)}
                                        extra={<span className="tr-ending">{r.endingTitle || '—'}</span>}
                                        score={<span className={`tr-pill ${r.isWin ? 'pass' : 'fail'}`}>{r.isWin ? 'Перемога' : 'Поразка'}</span>}
                                    />
                                ))}
                            </Group>
                        );
                    })}

                    {tab === 'quiz' && Object.entries(quizGroups).map(([name, items]) => {
                        const passed = items.filter(r => r.passed).length;
                        const avg = Math.round(items.reduce((s, r) => s + r.percentage, 0) / items.length);
                        return (
                            <Group key={name} icon="📝" name={name} isOpen={isOpen(name)} onToggle={() => toggleGroup(name)}
                                chips={<>
                                    <Chip>{items.length} спроб</Chip>
                                    <Chip color="green">{passed} здали</Chip>
                                    <Chip color="blue">~{avg}%</Chip>
                                </>}
                            >
                                {items.map(r => (
                                    <ResultCard key={r._id} onClick={() => openDetail('quiz', r)}
                                        passed={r.passed}
                                        avatarText={initials(r.studentLastName, r.studentName)}
                                        name={`${r.studentLastName} ${r.studentName}`}
                                        sub={r.studentPosition || '—'}
                                        city={r.studentCity}
                                        date={formatDate(r.completedAt)}
                                        score={<>
                                            <strong className={r.passed ? 'clr-green' : 'clr-red'}>{r.percentage}%</strong>
                                            <span className={`tr-pill ${r.passed ? 'pass' : 'fail'}`}>{r.passed ? 'Здано' : 'Не здано'}</span>
                                        </>}
                                    />
                                ))}
                            </Group>
                        );
                    })}

                    {tab === 'complex' && Object.entries(complexGroups).map(([name, items]) => {
                        const passed = items.filter(r => r.overallPassed).length;
                        return (
                            <Group key={name} icon="🧩" name={name} isOpen={isOpen(name)} onToggle={() => toggleGroup(name)}
                                chips={<>
                                    <Chip>{items.length} проходжень</Chip>
                                    <Chip color="green">{passed} пройшли</Chip>
                                    <Chip color="red">{items.length - passed} провалено</Chip>
                                </>}
                            >
                                {items.map(r => (
                                    <ResultCard key={r._id} onClick={() => openDetail('complex', r)}
                                        passed={r.overallPassed}
                                        avatarText={initials(r.studentLastName, r.studentName)}
                                        name={`${r.studentLastName} ${r.studentName}`}
                                        sub={r.studentPosition || '—'}
                                        city={r.studentCity}
                                        date={formatDate(r.completedAt)}
                                        extra={<span className="tr-steps-count">{r.steps?.length || 0} кроків</span>}
                                        score={<span className={`tr-pill ${r.overallPassed ? 'pass' : 'fail'}`}>{r.overallPassed ? 'Пройдено' : 'Не здано'}</span>}
                                    />
                                ))}
                            </Group>
                        );
                    })}



                    {tab === 'pizza' && Object.entries(pizzaGroups).map(([name, items]) => {
                        const passed = items.filter(r => r.passed).length;
                        const avg = Math.round(items.reduce((s, r) => s + r.percentage, 0) / items.length);
                        return (
                            <Group key={name} icon="🍕" name={name} isOpen={isOpen(name)} onToggle={() => toggleGroup(name)}
                                chips={<>
                                    <Chip>{items.length} спроб</Chip>
                                    <Chip color="green">{passed} здали</Chip>
                                    <Chip color="blue">~{avg}%</Chip>
                                </>}
                            >
                                {items.map(r => (
                                    <ResultCard key={r._id} onClick={() => openDetail('pizza', r)}
                                        passed={r.passed}
                                        avatarText={initials(r.studentLastName, r.studentName)}
                                        name={`${r.studentLastName} ${r.studentName}`}
                                        sub={r.studentPosition || '—'}
                                        city={r.studentCity}
                                        date={formatDate(r.completedAt || r.createdAt)}
                                        score={<>
                                            <strong className={r.passed ? 'clr-green' : 'clr-red'}>{r.percentage}%</strong>
                                            <span className={`tr-pill ${r.passed ? 'pass' : 'fail'}`}>{r.passed ? 'Здано' : 'Не здано'}</span>
                                        </>}
                                    />
                                ))}
                            </Group>
                        );
                    })}
                    {currentPagination.hasMore && (
                        <button className="tr-btn tr-load-more" onClick={handleLoadMore} disabled={loadingMore}>
                            {loadingMore ? 'Завантаження...' : `Показати ще${remainingCount ? ` (${remainingCount})` : ''}`}
                        </button>
                    )}
                </div>
            )}

            {/* AI Modal */}
            <DetailModal
                show={aiModalOpen}
                onClose={() => setAiModalOpen(false)}
                title="🤖 AI Аналізатор результатів"
            >
                <div className="tr-ai-content">
                    {aiLoading ? (
                        <div className="tr-ai-loading">
                            <div className="tr-spinner" />
                            <p>AI аналізує дані...</p>
                        </div>
                    ) : (
                        <>
                            {aiSummary?.scope && (
                                <div className="tr-ai-scope">
                                    <span>{aiSummary.scope.period}</span>
                                    <span>{aiSummary.scope.city}</span>
                                    <span>{aiSummary.scope.module}</span>
                                    <span>{aiSummary.totalResults} результатів</span>
                                    <span>Вибірка: {aiSummary.sampleQuality?.level || '—'}</span>
                                </div>
                            )}
                            <AiReportView content={aiAnalysis} />
                        </>
                    )}
                </div>
            </DetailModal>

            {/* Detail Modal */}
            <DetailModal
                show={!!detailItem}
                onClose={closeDetail}
                title={
                    detailType === 'desk' ? '🍽️ Деталі сервірування' :
                    detailType === 'game' ? '🎮 Деталі гри' :
                    detailType === 'quiz' ? '📝 Деталі квізу' :
                    detailType === 'complex' ? '🧩 Деталі комплексного тесту' :
                    '🍕 Деталі конструктора піци'
                }
            >
                {detailType === 'desk' && detailItem && <DeskDetail item={detailItem} cities={cities} canEdit={canEditCity} onCityChange={c => updateCity('desk', detailItem._id, c)} />}
                {detailType === 'game' && detailItem && <GameDetail item={detailItem} cities={cities} canEdit={canEditCity} onCityChange={c => updateCity('game', detailItem._id, c)} />}
                {detailType === 'quiz' && detailItem && <QuizDetail item={detailItem} cities={cities} canEdit={canEditCity} onCityChange={c => updateCity('quiz', detailItem._id, c)} />}
                {detailType === 'complex' && detailItem && <ComplexDetail item={detailItem} cities={cities} canEdit={canEditCity} onCityChange={c => updateCity('complex', detailItem._id, c)} />}
                {detailType === 'pizza' && detailItem && <PizzaDetail item={detailItem} cities={cities} canEdit={canEditCity} onCityChange={c => updateCity('pizza', detailItem._id, c)} />}
            </DetailModal>
        </div>
    );
};

const Chip = ({ children, color }) => (
    <span className={`tr-chip ${color || ''}`}>{children}</span>
);

export default TestResults;
